import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL, formatDate } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import PoweredBy from '../components/ui/PoweredBy';
import {
    Truck, MapPin, Navigation, CheckCircle2, Package, Loader2, Zap
} from 'lucide-react';

const PortalTourPage = () => {
    const { token } = useParams();
    const queryClient = useQueryClient();
    
    // Ajout de la config entreprise pour le Logo
    const [companyConfig, setCompanyConfig] = useState({ name: "Tournée en cours", logo: null });

    useEffect(() => {
        fetch(`${API_URL}/settings/public-config`)
            .then(res => res.json())
            .then(data => setCompanyConfig({ name: data.company_name, logo: data.logo_base64 }))
            .catch(() => {});
    }, []);

    const { data, isLoading, error } = useQuery({
        queryKey: ['portal-tour', token],
        queryFn: async () => {
            const res = await fetch(`${API_URL}/tours/public/${token}`);
            if (!res.ok) throw new Error('Tournée introuvable');
            return res.json();
        },
        enabled: !!token && token !== 'undefined',
    });

    const updateStatusMutation = useMutation({
        mutationFn: async ({ deliveryId, status }) => {
            const res = await fetch(`${API_URL}/tours/public/${token}/deliveries/${deliveryId}/status?status=${status}`, {
                method: 'POST',
            });
            if (!res.ok) throw new Error('Erreur lors de la mise à jour');
            return res.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['portal-tour', token]);
            toast.success('Statut mis à jour');
        },
        onError: (err) => toast.error(err.message),
    });

    if (isLoading) {
        return (
            <div className="min-h-screen bg-slate-50 p-6 space-y-4">
                <Skeleton className="h-24 w-full rounded-2xl" />
                <Skeleton className="h-40 w-full rounded-2xl" />
                <Skeleton className="h-40 w-full rounded-2xl" />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
                <Card className="w-full max-w-md text-center p-8 border-none shadow-xl bg-white text-slate-900 rounded-2xl">
                    <Truck className="w-16 h-16 mx-auto text-slate-300 mb-6" />
                    <h2 className="text-2xl font-bold mb-2">Tournée introuvable</h2>
                    <p className="text-slate-500">Le lien est invalide ou la tournée a été supprimée.</p>
                </Card>
            </div>
        );
    }

    const { tour, deliveries } = data;

    const openGPS = (address) => {
        const url = `http://maps.google.com/maps?q=${encodeURIComponent(address)}`;
        window.open(url, '_blank');
    };

    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-900 pb-20">
            {/* Header / NavBar Mobile */}
            <header className="bg-white sticky top-0 z-20 border-b border-slate-200 shadow-sm">
                <div className="max-w-2xl mx-auto px-4 py-4 flex flex-col gap-3">
                    {/* Logo Entreprise Centré */}
                    <div className="flex justify-center items-center gap-2 mb-2">
                        {companyConfig.logo ? (
                            <img src={companyConfig.logo} alt="Logo" className="h-8 w-auto object-contain" />
                        ) : (
                            <Zap className="w-5 h-5 text-primary" />
                        )}
                        <span className="font-bold text-slate-800">{companyConfig.name}</span>
                    </div>
                    
                    {/* Infos Tournée */}
                    <div className="flex justify-between items-center bg-slate-50 p-3 rounded-xl border border-slate-100">
                        <div className="flex items-center gap-3">
                            <div className="bg-primary/10 text-primary p-2.5 rounded-lg">
                                <Truck className="w-5 h-5" />
                            </div>
                            <div>
                                <h1 className="font-bold text-slate-900 leading-tight">{tour.reference}</h1>
                                <p className="text-xs text-slate-500 mt-0.5">{formatDate(tour.date)} • {tour.driver_name}</p>
                            </div>
                        </div>
                        <Badge variant="secondary" className="bg-slate-200 text-slate-700 font-bold px-3 py-1">
                            {deliveries.length} Arrêts
                        </Badge>
                    </div>
                </div>
            </header>

            <main className="max-w-2xl mx-auto p-4 space-y-5 mt-2">
                {deliveries.map((delivery, index) => (
                    <Card
                        key={delivery.id}
                        className={`overflow-hidden border-0 shadow-md transition-all duration-300 ${
                            delivery.status === 'delivered' 
                                ? 'bg-slate-50/50 opacity-75 ring-1 ring-emerald-500/20' 
                                : delivery.status === 'issue' 
                                ? 'ring-2 ring-red-400' 
                                : 'bg-white ring-1 ring-slate-200'
                        }`}
                    >
                        {/* Barre de statut visuelle */}
                        <div className={`h-1.5 w-full ${
                            delivery.status === 'delivered' ? 'bg-emerald-500' : 
                            delivery.status === 'issue' ? 'bg-red-500' : 'bg-primary'
                        }`} />
                        
                        <CardContent className="p-5 space-y-4">
                            {/* Ligne 1 : Numéro et Nom Client */}
                            <div className="flex justify-between items-start">
                                <div className="flex items-center gap-3">
                                    <span className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm shadow-sm ${
                                        delivery.status === 'delivered' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'
                                    }`}>
                                        {index + 1}
                                    </span>
                                    <h3 className="font-bold text-lg text-slate-900 leading-tight">{delivery.client_name}</h3>
                                </div>
                                <Badge className={
                                    delivery.status === 'delivered' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200' : 
                                    'bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-200'
                                } variant="outline">
                                    {delivery.status === 'delivered' ? 'Livré' : 'En attente'}
                                </Badge>
                            </div>

                            {/* Ligne 2 : Bouton Adresse GPS */}
                            <button
                                onClick={() => openGPS(delivery.address)}
                                className="w-full flex items-center gap-3 p-3.5 bg-slate-50 border border-slate-100 rounded-xl text-left hover:bg-slate-100 transition-colors group"
                            >
                                <div className="bg-white p-2 rounded-lg shadow-sm border border-slate-100 group-hover:scale-110 transition-transform">
                                    <MapPin className="w-4 h-4 text-primary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-slate-700 leading-snug line-clamp-2">{delivery.address}</p>
                                </div>
                                <Navigation className="w-5 h-5 text-slate-400 group-hover:text-primary transition-colors shrink-0" />
                            </button>

                            {/* Ligne 3 : Matériel */}
                            {delivery.items && delivery.items.length > 0 && (
                                <div className="bg-slate-50/80 border border-slate-100 rounded-xl p-4">
                                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
                                        <Package className="w-4 h-4 text-slate-400" /> Matériel à livrer
                                    </p>
                                    <ul className="text-sm space-y-2">
                                        {delivery.items.map((item, i) => (
                                            <li key={i} className="flex justify-between items-center bg-white p-2 rounded-lg border border-slate-100 shadow-sm">
                                                <span className="font-medium text-slate-700">{item.name}</span>
                                                <span className="font-mono font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-md">x{item.quantity}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="pt-2">
                                {delivery.status !== 'delivered' ? (
                                    <Button
                                        className="w-full h-14 text-md font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-md hover:shadow-lg transition-all"
                                        onClick={() => updateStatusMutation.mutate({ deliveryId: delivery.id, status: 'delivered' })}
                                        disabled={updateStatusMutation.isPending}
                                    >
                                        {updateStatusMutation.isPending ? <Loader2 className="animate-spin mr-2" /> : <CheckCircle2 className="mr-2 h-6 w-6" />}
                                        Confirmer la livraison
                                    </Button>
                                ) : (
                                    <Button
                                        variant="outline"
                                        className="w-full h-12 text-slate-500 hover:text-slate-700 hover:bg-slate-100 border-slate-200"
                                        onClick={() => updateStatusMutation.mutate({ deliveryId: delivery.id, status: 'pending' })}
                                    >
                                        Annuler la validation
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ))}

                {deliveries.length === 0 && (
                    <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-slate-300">
                        <Truck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500 font-medium">Aucun arrêt prévu dans cette tournée.</p>
                    </div>
                )}
                
                {/* FOOTER AVEC POWERED BY */}
                <div className="mt-12 flex justify-center pb-8">
                    <PoweredBy />
                </div>
            </main>

            <Toaster richColors position="top-center" />
        </div>
    );
};

export default PortalTourPage;